import Service from '../service';
import Request from '../util/request';

export default class Auth {
    public verified: string | null = null;

    public timestamp: number = 0;
    public status: any = null;
    public loading: any = null;
    public session: any = {};

    private readonly logoutStorageKey: string = 'autocheck.auth.logout';
    private readonly externalCheckInterval: number = 3000;
    private externalCheckInFlight: boolean = false;
    private externalCheckTimestamp: number = 0;

    constructor(public service: Service) {
        this.request = new Request();
        this.bindSessionLifecycle();
    }

    private bindSessionLifecycle() {
        if (typeof window === 'undefined') return;

        window.addEventListener('storage', (event: StorageEvent) => {
            if (event.key !== this.logoutStorageKey || !event.newValue) return;
            this.clearClientSession();
            this.redirectTo('/access');
        });

        window.addEventListener('focus', () => {
            this.revalidate();
        });

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') this.revalidate();
            });
        }
    }

    private safeLocalPath(value: any, fallback: string = '/access'): string {
        const target = String(value || fallback).trim();
        if (!target.startsWith('/') || target.startsWith('//')) return fallback;
        return target;
    }

    private isAccessPage(): boolean {
        if (typeof location === 'undefined') return false;
        return String(location.pathname || '').replace(/\/+$/, '') === '/access';
    }

    private clearClientSession() {
        this.verified = null;
        this.timestamp = new Date().getTime();
        this.status = false;
        this.loading = true;
        this.session = {};
        try {
            this.service.render();
        } catch (e) { }
    }

    private broadcastLogout() {
        try {
            localStorage.setItem(
                this.logoutStorageKey,
                `${new Date().getTime()}:${Math.random().toString(36).slice(2)}`
            );
        } catch (e) { }
    }

    private redirectTo(value: any) {
        if (typeof location === 'undefined') return;
        const target = this.safeLocalPath(value);
        if (this.isAccessPage() && target === '/access') return;
        location.replace(target);
    }

    public logout(returnTo: string = '/access', event?: Event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const target = this.safeLocalPath(returnTo);
        this.clearClientSession();
        this.broadcastLogout();

        const query = new URLSearchParams({
            returnTo: target,
            _: String(new Date().getTime()),
        });
        location.replace(`/auth/oidc/logout?${query.toString()}`);
    }

    public async revalidate(force: boolean = false) {
        if (this.isAccessPage() || this.externalCheckInFlight) return this.status;

        const now = new Date().getTime();
        if (!force && now - this.externalCheckTimestamp < this.externalCheckInterval) {
            return this.status;
        }

        this.externalCheckInFlight = true;
        this.externalCheckTimestamp = now;
        try {
            let { code, data } = await this.request.post('/auth/check');
            if (code != 200 || !data) return this.status;

            this.timestamp = new Date().getTime();
            this.loading = true;
            this.session = data.session || {};
            this.status = data.status === true;
            this.verified = this.session.verified || null;

            if (!this.status) {
                this.clearClientSession();
                this.redirectTo('/access');
                return false;
            }

            try {
                await this.service.render();
            } catch (e) { }
            return true;
        } catch (e) {
            return this.status;
        } finally {
            this.externalCheckInFlight = false;
        }
    }

    public async init() {
        try {
            let { code, data } = await this.request.post('/auth/check');
            this.loading = true;
            if (code != 200 || !data) return this;

            const session = data.session || {};
            this.verified = session.verified || null;
            this.timestamp = new Date().getTime();
            this.session = session;
            this.status = data.status === true;
        } catch (e) {
            this.loading = true;
        }
        return this;
    }

    public async update() {
        while (this.loading === null) {
            await this.service.render(100);
        }

        let diff = new Date().getTime() - this.timestamp;
        if (diff > 1000 * 60) {
            this.loading = null;
            await this.init();
        }
    }

    public check: any = new Proxy(((root) => {
        let obj: any = () => {
            return this.status;
        }

        obj.root = root;
        return obj;
    })(this), {
        get(target, propKey) {
            let propValue: any = target.root.session[propKey];

            return (values: any = null) => {
                if (values === null) {
                    return true;
                }

                if (typeof values == 'boolean') {
                    if (values === propValue)
                        return true;
                    return false;
                }

                if (typeof values == 'string')
                    values = [values];

                if (values.indexOf(propValue) >= 0) {
                    return true;
                }

                return false;
            };
        }
    });

    public allow: any = new Proxy(((root) => {
        let obj: any = (redirect: any = null) => {
            if (root.check()) return true;
            if (redirect) location.href = redirect;
            return false;
        }

        obj.root = root;
        obj.check = root.check;
        return obj;
    })(this), {
        get(target, propKey) {
            return (values: any = null, redirect: any = null) => {
                if (target.check[propKey](values)) return true;
                if (redirect) location.href = redirect;
                return false;
            }
        }
    });

    public hash(password: string = '') {
        return this.service.crypto.SHA256(password).toString();
    }
}
